/**
 * Functions の画面の「API キーやパスワード」の欄（2026-10-04 console/secret-input・founder y）。
 * 判断は functionSecrets.ts（+ test）。サーバー: PUT / DELETE …/environments/:env/functions/secrets/:name（modules/compute/serviceSecrets.ts）。
 *
 * WHY 画面で値を入れるか（それまでは名前の一覧だけ）: 値を入れる道が AI のチャットか端末しか無いと、エンジニアでない人はチャットに貼る
 *   = 会話の記録に残る。ここで入れれば、値はブラウザ → 本体 → 実行基盤だけを通る（本体も保存しない）。
 *   MCP の functions_secret_set は既定で値を受けず、ここへのリンク（…/functions?secret=NAME#secrets）を返す。
 *
 * 値の扱い（functionSecrets.test.ts がこのソースを読んで確かめる）:
 *   - 入力は type="password"。パスワード管理の保存の提案を出さない（autoComplete="off"・1Password / LastPass の無視の印）
 *     = ダッシュボードのログインのパスワードと取り違えて保存・自動入力されるのを防ぐ
 *   - フォームは method="post": React が止まって既定の送信が走っても、値が URL（?value=…）に乗らない
 *   - 送ったら成功でも失敗でも欄を空にする（finally）。WHY 失敗でも消すか: 確認で「この画面には表示しません」と言った約束を
 *     例外なく守るため（開いたままの画面に値が残る時間を最短にする）。打ち直しは貼り直すだけ
 *   - ログ（console）に出さない。確認の画面にも名前だけを出す
 * 本番は確認を挟む（confirmBeforePut）= 今の確認の決まり（本番への変更は本人がブラウザで押す）。開発用は挟まない。消すときは常に確認。
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { api, ApiError } from '../api';
import { errorText, useT, type MessageKey } from '../i18n';
import {
  cleanSecretName,
  confirmBeforeDelete,
  confirmBeforePut,
  secretErrorKey,
  secretFromSearch,
  secretNameIssue,
  secretPath,
  secretValueIssue
} from '../functionSecrets';
import { ago, ConfirmDialog, fullDate } from './ui';
import { withUrlKey } from '../envWhere';
import { useShell } from '../pages/ProjectShell';
import { useIsOwner } from './OwnerOnly';

type Env = 'development' | 'production';

export function FunctionSecretsCard({
  base,
  env,
  secrets,
  onChanged
}: {
  /** `…/environments/:env/functions` */
  base: string;
  env: Env;
  secrets: Array<{ name: string; updatedAt: string }>;
  onChanged: () => void;
}) {
  const { t } = useT();
  const { envUrls } = useShell();
  const isOwner = useIsOwner();
  const location = useLocation();
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  // 上書き中の名前（一覧の「上書き」から来た）。null = 新しく足す
  const [replacing, setReplacing] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirmPut, setConfirmPut] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const valueRef = useRef<HTMLInputElement>(null);

  // AI が渡したリンク（?secret=NAME#secrets）から来たら、名前を入れて値の欄へ。名前は値ではないので URL に入ってよい
  useEffect(() => {
    const fromLink = secretFromSearch(location.search);
    if (fromLink) {
      setName(fromLink);
      setReplacing(secrets.some((s) => s.name === fromLink) ? fromLink : null);
    }
    if (location.hash === '#secrets' || fromLink) {
      document.getElementById('secrets')?.scrollIntoView({ block: 'start' });
      valueRef.current?.focus();
    }
    // 一覧の読み直しのたびに動かさない（最初に来たときだけ）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.hash]);

  // 環境を切り替えたら、入れかけの値と知らせを残さない（別の環境に送らない）
  useEffect(() => {
    setValue('');
    setNotice(null);
    setErr(null);
    setConfirmPut(false);
    setDeleting(null);
  }, [base]);

  const cleaned = cleanSecretName(name);
  const nameIssue = secretNameIssue(cleaned);
  const valueIssue = secretValueIssue(value);
  const canSave = !busy && nameIssue === null && valueIssue === null;

  // 本体のエラー → この欄の言い方（functionSecrets.ts secretErrorKey）。知らない code は共通の errorText
  const explain = (e: unknown) => {
    if (!(e instanceof ApiError)) return String(e);
    const key = secretErrorKey(e.code);
    // balance_due の言い方は Functions の画面と同じ（FunctionsPage の err の表示）
    const overrides: Partial<Record<string, MessageKey>> = { balance_due: 'error.prepaid_required' };
    if (key) overrides[e.code] = key as MessageKey;
    return errorText(e, overrides);
  };

  const put = async () => {
    const target = cleaned;
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      await api(secretPath(base, target), { method: 'PUT', body: { value } });
      setNotice(t('fnSecret.saved', { name: target }));
      setName('');
      setReplacing(null);
      setTouched(false);
      onChanged();
    } catch (e) {
      setErr(explain(e));
    } finally {
      // 値は成功でも失敗でも消す（再表示しない・確認で「この画面には表示しません」と言った約束）
      setValue('');
      setBusy(false);
      setConfirmPut(false);
    }
  };

  const remove = async (target: string) => {
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      await api(secretPath(base, target), { method: 'DELETE' });
      setNotice(t('fnSecret.deleted', { name: target }));
      if (replacing === target) setReplacing(null);
    } catch (e) {
      setErr(explain(e));
    } finally {
      setBusy(false);
      setDeleting(null);
      // 404（もう無い）でも一覧を読み直す = 画面と本体をそろえる
      onChanged();
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSave) return;
    if (confirmBeforePut(env)) setConfirmPut(true);
    else void put();
  };

  const startReplace = (n: string) => {
    setName(n);
    setReplacing(n);
    setValue('');
    setNotice(null);
    setErr(null);
    valueRef.current?.focus();
  };

  const envNote = withUrlKey(`fnSecret.envNote.${env}` as const, envUrls[env] ?? null);

  return (
    <section className="card" id="secrets">
      <h2 className="card-title">{t('fnSecret.title')}</h2>
      <p className="muted small">{t('fnSecret.lead')}</p>
      <div className="alert alert-info" role="note">
        <span>{t('fnSecret.chatWarning')}</span>
      </div>
      {/* どのアプリのサーバーが使う値かを本物の URL で言う（2026-10-04 console/plain-words-3・founder「抽象的にしないで」）。
          以前は「{env}で使う値です」で、{env} =「みんなが使うほう」という造語だった */}
      <p className="muted small">{t(envNote.key, envNote.vars)}</p>

      {secrets.length === 0 ? (
        <p className="muted small">{t('fnSecret.empty')}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <tbody>
              {secrets.map((s) => (
                <tr key={s.name}>
                  {/* 更新の時刻は名前の下に置く（スマホの幅で列にすると「昨日に更新」が 1 文字ずつ折れるため） */}
                  <td>
                    <div className="mono">{s.name}</div>
                    <div className="small muted" title={fullDate(s.updatedAt)}>
                      {t('fnSecret.updated', { ago: ago(s.updatedAt) })}
                    </div>
                  </td>
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => startReplace(s.name)} disabled={busy}>
                      {t('fnSecret.replace')}
                    </button>{' '}
                    {/* 秘密を消すのは持ち主だけ（動いている Functions を止めうる・2026-10-22 core/handoff-1b）。入れる・上書きは共同作業者も */}
                    {isOwner && (
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setDeleting(s.name)} disabled={busy}>
                        {t('fnSecret.delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* method="post": JS が止まって既定の送信が走っても、値を URL に乗せない（既定の GET だと ?value=… になる） */}
      <form method="post" onSubmit={submit} autoComplete="off">
        {replacing && (
          <p className="small">
            {t('fnSecret.replacing', { name: replacing })}{' '}
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                setReplacing(null);
                setName('');
                setValue('');
              }}
            >
              {t('fnSecret.cancelReplace')}
            </button>
          </p>
        )}
        <div className="form-grid form-grid-top">
          {!replacing && (
            <label className="field">
              <span>{t('fnSecret.name')}</span>
              <input
                className="mono"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setName((n) => cleanSecretName(n))}
                placeholder="STRIPE_SECRET_KEY"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={80}
              />
              <small className={touched && nameIssue ? 'field-error' : 'muted'}>
                {touched && nameIssue ? t(`fnSecret.nameIssue.${nameIssue}`) : t('fnSecret.nameHint')}
              </small>
            </label>
          )}
          <label className="field">
            <span>{t('fnSecret.value')}</span>
            {/* パスワード管理に保存・自動入力させない（ダッシュボードのパスワードと取り違える）: autoComplete="off" と各製品の無視の印 */}
            <input
              ref={valueRef}
              type="password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t('fnSecret.valuePlaceholder')}
              autoComplete="off"
              spellCheck={false}
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
            />
            {touched && valueIssue && <small className="field-error">{t(`fnSecret.valueIssue.${valueIssue}`)}</small>}
          </label>
        </div>
        {notice && (
          <p className="alert alert-ok" role="status">
            {notice}
          </p>
        )}
        {err && (
          <p className="alert alert-error" role="alert">
            {err}
          </p>
        )}
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy}>
            {busy ? t('common.working') : t('fnSecret.save')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmPut}
        title={t('fnSecret.confirmPutTitle', { name: cleaned })}
        confirmLabel={t('fnSecret.confirmPutButton')}
        tone="danger"
        busy={busy}
        onClose={() => setConfirmPut(false)}
        onConfirm={() => void put()}
      >
        <p>{t('fnSecret.confirmPutBody')}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={deleting !== null && confirmBeforeDelete(env)}
        title={t('fnSecret.confirmDeleteTitle', { name: deleting ?? '' })}
        confirmLabel={t('fnSecret.confirmDeleteButton')}
        tone={env === 'production' ? 'danger' : 'default'}
        busy={busy}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && void remove(deleting)}
      >
        <p>{t('fnSecret.confirmDeleteBody')}</p>
      </ConfirmDialog>
    </section>
  );
}
